/**
 * TableCard — Phase E grid card + Phase F interaction layer.
 *
 * Phase F additions:
 *   - Pre-breakfast tap → toggles selection (blue border #2196F3)
 *   - Post-breakfast tap → opens TableDialog (status-aware)
 *
 * CR-0025 (Δ-23, 2026-06-01) — the inline Clear bar on Occupied cards
 * is no longer interactive. The dialog's Clear button is the only
 * actionable clear path. `useClearTable` is therefore no longer
 * imported here; mutations are wired up inside `OccupiedForm`.
 */
import { useState } from "react";
import { getCardColors } from "@/utils/categories";
import CategoryDots from "@/components/card/CategoryDots";
import AvailableCard from "@/components/card/AvailableCard";
import ReservedCard from "@/components/card/ReservedCard";
import OccupiedCard from "@/components/card/OccupiedCard";
import TableDialog from "@/components/dialog/TableDialog";
import useAppStore from "@/store/useAppStore";
import useSelectionStore from "@/store/useSelectionStore";

const SELECTION_COLOR = "#2196F3";

export default function TableCard({ table }) {
  const { background, border } = getCardColors(table.categories);
  const hasCat = table.categories && table.categories.length > 0;

  const breakfastStarted = useAppStore((s) => s.appState?.breakfast_started);
  const isSelected = useSelectionStore((s) =>
    s.selectedCodes.includes(table.code),
  );
  const toggleSelect = useSelectionStore((s) => s.toggle);

  const [dialogOpen, setDialogOpen] = useState(false);

  const handleCardClick = () => {
    if (!breakfastStarted) {
      toggleSelect(table.code);
    } else {
      setDialogOpen(true);
    }
  };

  let content;
  switch (table.status) {
    case "reserved":
      content = <ReservedCard table={table} />;
      break;
    case "occupied":
      content = <OccupiedCard table={table} />;
      break;
    default:
      content = <AvailableCard table={table} />;
  }

  // Selection border (blue) overrides category border per Δ-04 §2.6
  const effectiveBorderColor = isSelected ? SELECTION_COLOR : border;
  const effectiveBorderWidth = isSelected || hasCat ? "2px" : "1px";

  return (
    <>
      <button
        type="button"
        data-testid={`table-card-${table.code}`}
        data-selected={isSelected || undefined}
        onClick={handleCardClick}
        className="relative rounded-2xl overflow-hidden text-left w-full h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        style={{
          backgroundColor: background,
          border: `${effectiveBorderWidth} solid ${effectiveBorderColor}`,
          boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
        }}
      >
        {content}

        {/* Category dots — beside the code badge */}
        {hasCat && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
            <CategoryDots categories={table.categories} />
          </div>
        )}
      </button>

      <TableDialog
        table={table}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
}
